import React, { useEffect, useState } from 'react'
import { IActiveTripResponseV2, IOrgObj, IRecentTripObj, IRecentTrips, IUserTopPanelRider } from 'src/types'
import { riderService } from 'src/services/Rider'
import { Route } from 'react-router'
import ImageView from '../../ui/ImageView'
import { Link } from 'react-router-dom'
import { formatPhone } from 'src/utils'
import WalletBalance from 'src/containers/riders/riderdetails/WalletBalance'
import { BALANCE_TYPES } from 'src/constants/wallet'
import AddFundsModal from 'src/containers/riders/riderdetails/AddFundsModal'
import AddCardModal from 'src/containers/riders/riderdetails/AddCardModal'
import { MoonLoader } from 'react-spinners'
import { tripService } from 'src/services/Trip'
import StatusTag from '../../ui/StatusTag'
import moment from 'moment'
import Copy from '../../icons/ui/Copy'
import organizationsService from 'src/services/Organizations'
import TimelineViewCreateTripPane from 'src/containers/trips/TimelineViewCreateTripPane'
import { useDispatch } from 'react-redux'
import { actionCreators as googleActionCreators } from '../../../state/actions/google_map'
import * as tripActions from '../../../state/actions/trips'

interface ITimelineViewRiderTopPanelProps {
  riderId: string
  orgId: string
}

interface IItemProps {
  trip: IRecentTripObj
  showCreateTripPane: boolean
  setClonedTrip: (value: any) => void
  setShowCreateTripPane: (value: boolean) => void
}

const RecentTripItem = (props: IItemProps) => {
  const { actionCreators: tripActionCreators } = tripActions
  const { trip, setClonedTrip, setShowCreateTripPane, showCreateTripPane } = props
  const dispatch = useDispatch()
  const status = trip.lastStatus
  const statusAt = trip.lastStatusAt
  const pickup = (trip.pickupLocation && trip.pickupLocation.fullAddress) || ''
  const dropoff = (trip.dropoffLocation && trip.dropoffLocation.fullAddress) || ''

  const dispatchCloneTrip = async (trip: IActiveTripResponseV2, useTripNotes?: boolean) => {
    const tripClone = { ...trip }
    const pickup = {
      lat: trip.pickupLocation.latitude,
      lng: trip.pickupLocation.longitude
    }
    const dropoff = {
      lat: trip.dropoffLocation.latitude,
      lng: trip.dropoffLocation.longitude
    }
    tripClone.scheduledDropoffTime = undefined
    tripClone.notes = useTripNotes !== false ? tripClone.notes : undefined // choose between displayin rider notes or the custom trip notes
    tripClone.confirmationNumber = undefined
    tripClone.providerOneNumber = undefined
    tripClone.tripRequester = trip.tripRequester
    return Promise.all([
      dispatch(tripActionCreators.cloneTrip(tripClone)),
      dispatch(googleActionCreators.setOrigin(pickup)),
      dispatch(googleActionCreators.setDestination(dropoff)),
      dispatch(googleActionCreators.replaceMarkers('origin', [{ type: 'origin', lat: pickup.lat, lng: pickup.lng }])),
      dispatch(
        googleActionCreators.replaceMarkers('destination', [
          { type: 'destination', lat: dropoff.lat, lng: dropoff.lng }
        ])
      ),
      dispatch(
        tripActionCreators.setEstimatesToTripRequest({
          duration: Math.round(tripClone.estimatedDuration),
          distance: Math.round(tripClone.estimatedDistance)
        })
      ),
      dispatch(
        tripActionCreators.setTripPassengerDetails({
          tripRequestRiders: tripClone.tripsGroupRides,
          disabilityApproved: false,
          attendant: false
        })
      ),
      dispatch(tripActionCreators.updateRiderCount(tripClone.riderCount || 1))
    ])
  }

  const cloneTrip = async () => {
    try {
      const retrievedTrip = await tripService.get(trip.id)
      await dispatchCloneTrip(retrievedTrip)
      setClonedTrip(retrievedTrip)
      setShowCreateTripPane(true)
    } catch (e) {
      console.log('Clone trip error: ', e)
    }
  }

  return (
    <div className='flex justify-between cursor--default pt3'>
      <div key={`${trip.id}-${trip.service.serviceId}`} className='recent-trips-body'>
        <div className='flex justify-between'>
          <div className='flex items-center'>
            <ImageView entityId={trip.service.serviceId} width='30px' height='30px' />
            <Link className='' to={`/trips/${trip.id}`}>
              <p className='mt0 mb0 ml1 h5 font-black'>{trip.service.name}</p>
            </Link>
          </div>
          {!showCreateTripPane ? (
            <Copy className='ml1 pt1 cursor--pointer' onClick={cloneTrip} />
          ) : (
            <Copy className='ml1 pt1 cursor--not-allowed' />
          )}
        </div>
        <div className='flex items-center pt1'>
          <span className='h5'>{pickup.split(',')[0]}</span>
          <span className='font-black mr1 ml1'>&#8594;</span>
          <span className='h5'>{dropoff.split(',')[0]}</span>
        </div>
        {status ? (
          <div className='flex items-center justify-between pt2'>
            <p className='mt0 mb0'>
              <StatusTag status={status} />
            </p>
            <p className='m0 h6 bold font-blue'>{moment(statusAt).fromNow()}</p>
          </div>
        ) : null}
      </div>
    </div>
  )
}

const TimelineViewSelectedRider = (props: ITimelineViewRiderTopPanelProps) => {
  const { riderId, orgId } = props
  const [rider, setRider] = useState<IUserTopPanelRider | null>(null)
  const [recentTrips, setRecentTrips] = useState<IRecentTrips | null>(null)
  const [showCreateTripPane, setShowCreateTripPane] = useState<boolean>(false)
  const [organization, setOrganization] = useState<IOrgObj | null>(null)
  const [clonedTrip, setClonedTrip] = useState<any>(null)

  useEffect(() => {
    getRiderPersonalInfo(riderId)
    retrieveOrganization(orgId)
  }, [])

  useEffect(() => {
    if (rider) {
      retrieveRiderRecentTrips(rider.id, orgId)
    }
  }, [rider])

  const getRiderPersonalInfo = async (riderId: string) => {
    try {
      const rider = await riderService.getRiderPersonalInfo(riderId)
      setRider(rider)
    } catch (error) {
      console.log(error)
    }
  }

  const addFundsModal = (props: any) => {
    return <AddFundsModal rider={rider} {...props} />
  }
  const addCardModal = (props: any) => {
    return <AddCardModal rider={rider} {...props} />
  }

  const getFormattedPhoneNumber = () => {
    if (rider) {
      const { phone = '', codeCountry } = rider
      const formattedPhoneNumber = formatPhone(phone, codeCountry)
      return formattedPhoneNumber
    } else {
      return ''
    }
  }

  const retrieveRiderRecentTrips = async (id: string, orgId: string) => {
    try {
      const recentTrips: IRecentTrips = await tripService.fetchRecentTrips(id, orgId)
      setRecentTrips(recentTrips)
    } catch (error) {
      console.log(error)
    }
  }

  const retrieveOrganization = async (orgId: string) => {
    try {
      const organization = await organizationsService.getSingleOrg(orgId)
      setOrganization(organization)
    } catch (error) {
      console.log(error)
    }
  }

  const renderToday = (trips: IRecentTripObj[]) => {
    if (!trips.length) {
      return (
        <div className='flex justify-center h5 mt2'>
          <span className='h5'>No Trips Today</span>
        </div>
      )
    }
    return (
      <div>
        {trips.map(t => {
          return (
            <RecentTripItem
              key={t.id}
              trip={t}
              setClonedTrip={setClonedTrip}
              setShowCreateTripPane={setShowCreateTripPane}
              showCreateTripPane={showCreateTripPane}
            />
          )
        })}
      </div>
    )
  }

  const renderRecent = (trips: IRecentTripObj[]) => {
    if (!trips.length) {
      return (
        <div className='flex justify-center h5 mt2'>
          <span className='h5'>No Recent Trips</span>
        </div>
      )
    }
    return (
      <div>
        {trips.map(t => {
          return (
            <RecentTripItem
              key={t.id}
              trip={t}
              setClonedTrip={setClonedTrip}
              setShowCreateTripPane={setShowCreateTripPane}
              showCreateTripPane={showCreateTripPane}
            />
          )
        })}
      </div>
    )
  }

  const params = {
    params: {
      riderId
    }
  }

  return (
    <section>
      {rider ? (
        <>
          <Route exact={true} path={`/scheduler/add-funds`} render={addFundsModal} />
          <Route exact={true} path={`/scheduler/add-funds/add-card`} render={addCardModal} />
          <div className='relative'>
            <div className='fit flex flex-column'>
              <div className='flex flex-row'>
                <div className='center'>
                  {/* Declare name of Avatar */}
                  <div>
                    <ImageView
                      entityId={riderId}
                      isRounded={true}
                      width='30px'
                      height='30px'
                      name={rider ? `${rider.firstName} ${rider.lastName}` : ''}
                    />
                  </div>
                  <Link className='font-small font-blue' to={`/riders/${riderId}/profile/info`}>
                    Profile
                  </Link>
                </div>
                <div className='flex flex-wrap col-12 ml1 justify-between content-center'>
                  <div className='flex flex-column'>
                    <div className='h5 col-12 font-dark-grey flex justify-start'>{`${rider.firstName} ${
                      rider.lastName
                    }`}</div>
                    <div className='h5 col-12 font-grey2 text-start'>{getFormattedPhoneNumber()}</div>
                    <div className='left-align'>
                      <WalletBalance
                        rider={rider}
                        riderId={riderId}
                        balanceType={BALANCE_TYPES.OVERALL}
                        actionUrl={`/scheduler/add-funds`}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className='fit flex'>
              <div className='col-12'>
                {/* //Note */}
                {rider.internalNote ? (
                  <div className='col-12 font-grey2 center pt1'>
                    <b>Internal Note: </b>
                    {rider.internalNote}
                  </div>
                ) : null}
              </div>
            </div>

            <div className='tmln-view-rider-button-container pt2'>
              {!showCreateTripPane ? (
                <button
                  type='button'
                  onClick={() => setShowCreateTripPane(true)}
                  className='SubmitButton custom-btn mbsc-blue-btn'
                >
                  Book New Ride
                </button>
              ) : (
                <button type='button' className='SubmitButton custom-btn mbsc-gray-btn cursor--not-allowed'>
                  Book New Ride
                </button>
              )}
            </div>

            <div className='pt2'>
              <div className='timeline-view-recent-trips-header'>UPCOMING</div>
              <div>
                {recentTrips ? (
                  renderToday(recentTrips.today)
                ) : (
                  <div className='col-12 flex px-auto py2 justify-center'>
                    <MoonLoader sizeUnit={'px'} size={20} color={'#405264'} loading={true} />
                  </div>
                )}
              </div>
            </div>

            <div className='pt2'>
              <div className='timeline-view-recent-trips-header'>RECENT</div>
              <div>
                {recentTrips ? (
                  renderRecent(recentTrips.recent)
                ) : (
                  <div className='col-12 flex px-auto py2 justify-center'>
                    <MoonLoader sizeUnit={'px'} size={20} color={'#405264'} loading={true} />
                  </div>
                )}
              </div>
            </div>
          </div>
          {showCreateTripPane && (
            <div className='timeline-view-create-trip-pane p2'>
              <TimelineViewCreateTripPane match={params} setShowCreateTripPane={setShowCreateTripPane} />
            </div>
          )}
        </>
      ) : (
        <div className='col-12 flex px-auto py2 justify-center'>
          <MoonLoader sizeUnit={'px'} size={20} color={'#405264'} loading={true} />
        </div>
      )}
    </section>
  )
}

export default TimelineViewSelectedRider



// WEBPACK FOOTER //
// ./src/components/trips/timeline-view/TimelineViewSelectedRider.tsx